import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { videoAdsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Video, Zap, Users, CheckCircle, Clock, XCircle, 
  Loader2, CreditCard, Coins
} from 'lucide-react';
import { toast } from 'sonner';

const VideoAds = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [myAds, setMyAds] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    youtube_url: '',
    title: '',
    description: '',
    payment_method: 'credits'
  });

  // Handle payment return URL params
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      toast.success('Payment successful! Your video ad has been submitted for review.');
      setSearchParams({});
      fetchData();
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled.');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [adsRes, pricingRes] = await Promise.all([
        videoAdsAPI.getMyAds(),
        videoAdsAPI.getPricing()
      ]);
      setMyAds(adsRes.data.video_ads || []);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.youtube_url || !formData.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (formData.payment_method === 'stripe') {
        // Create Stripe checkout session
        const response = await videoAdsAPI.createCheckout(formData);
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      } else {
        // Pay with credits
        const response = await videoAdsAPI.submitAd(formData);
        toast.success(response.data.message);
        setDialogOpen(false);
        setFormData({ youtube_url: '', title: '', description: '', payment_method: 'credits' });
        fetchData();
        refreshUser(); // Refresh user to update credits
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit video ad');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-neon-mint" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending Review';
      case 'approved':
        return 'Approved - Will be in next newsletter';
      case 'rejected':
        return 'Rejected';
      case 'sent':
        return 'Sent in Newsletter';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-neon-mint" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white mb-2">
            Newsletter Video Ads
          </h1>
          <p className="text-gray-400">
            Get your YouTube video featured in our newsletter to all members!
          </p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
        >
          <Video className="w-4 h-4 mr-2" />
          Submit Video Ad
        </Button>
      </div>

      {/* Pricing Card */}
      {pricing && (
        <Card className="bg-gradient-to-r from-neon-violet/20 to-neon-mint/20 border-neon-violet/30">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                  <Video className="w-6 h-6 text-neon-violet" />
                  Feature Your Video - ${pricing.dollars_price}
                </h2>
                <p className="text-gray-300 mb-4">{pricing.description}</p>
                <div className="grid grid-cols-2 gap-3">
                  {pricing.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-neon-mint" />
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="bg-void/50 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Pay with Credits</p>
                  <p className="text-2xl font-bold text-neon-mint">{pricing.credits_price} credits</p>
                </div>
                <div className="bg-void/50 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Pay with Card</p>
                  <p className="text-2xl font-bold text-white">${pricing.dollars_price}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Video Ads */}
      <Card className="bg-void-paper border-white/10">
        <CardHeader>
          <CardTitle className="text-white">My Video Ad Submissions</CardTitle>
        </CardHeader>
        <CardContent>
          {myAds.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">No video ads submitted yet.</p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-neon-mint text-black"
              >
                Submit Your First Video
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {myAds.map((ad) => (
                <div 
                  key={ad.id}
                  className="flex items-center gap-4 p-4 bg-void rounded-lg border border-white/10"
                >
                  <div className="w-12 h-12 rounded-lg bg-neon-violet/20 flex items-center justify-center">
                    <Video className="w-6 h-6 text-neon-violet" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{ad.title}</h3>
                    <p className="text-gray-500 text-sm truncate">{ad.youtube_url}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(ad.status)}
                    <span className={`text-sm ${
                      ad.status === 'approved' ? 'text-green-500' :
                      ad.status === 'rejected' ? 'text-red-500' :
                      ad.status === 'sent' ? 'text-neon-mint' : 'text-yellow-500'
                    }`}>
                      {getStatusText(ad.status)}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-xs">
                      {new Date(ad.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Video className="w-5 h-5 text-neon-violet" />
              Submit Video Ad
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white">YouTube URL *</Label>
              <Input
                placeholder="https://youtube.com/watch?v=..."
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                className="mt-1 bg-void border-white/10"
                required
              />
            </div>
            
            <div>
              <Label className="text-white">Video Title *</Label>
              <Input
                placeholder="Enter a catchy title for your ad"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 bg-void border-white/10"
                required
              />
            </div>
            
            <div>
              <Label className="text-white">Description (Optional)</Label>
              <Textarea
                placeholder="Brief description of your video..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 bg-void border-white/10"
                rows={3}
              />
            </div>
            
            <div>
              <Label className="text-white mb-3 block">Payment Method</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payment_method: 'credits' })}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.payment_method === 'credits'
                      ? 'border-neon-mint bg-neon-mint/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <Coins className={`w-6 h-6 mx-auto mb-2 ${
                    formData.payment_method === 'credits' ? 'text-neon-mint' : 'text-gray-400'
                  }`} />
                  <p className="font-bold text-white">{pricing?.credits_price || 200} Credits</p>
                  <p className="text-xs text-gray-400">You have {user?.credits || 0}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payment_method: 'stripe' })}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.payment_method === 'stripe'
                      ? 'border-neon-violet bg-neon-violet/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <CreditCard className={`w-6 h-6 mx-auto mb-2 ${
                    formData.payment_method === 'stripe' ? 'text-neon-violet' : 'text-gray-400'
                  }`} />
                  <p className="font-bold text-white">${pricing?.dollars_price || 20}</p>
                  <p className="text-xs text-gray-400">Pay with card</p>
                </button>
              </div>
            </div>
            
            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setDialogOpen(false)}
                className="text-gray-400"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={submitting}
                className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Submit & Pay
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoAds;
